import React from 'react';
import TeamLogo from './TeamLogo';

const PHASE_ORDER = ['ROUND_OF_16', 'QUARTER_FINAL', 'SEMI_FINAL', 'FINAL'];
const PHASE_SHORT = {
  ROUND_OF_16:   'Oitavas',
  QUARTER_FINAL: 'Quartas',
  SEMI_FINAL:    'Semifinal',
  FINAL:         'Final',
};

const PHASE_MIN_TEAMS = { ROUND_OF_16: 16, QUARTER_FINAL: 8, SEMI_FINAL: 4, FINAL: 2 };

/* Troféu do clube (SVG) — o mesmo glifo usado no restante do campeonato */
const TrophyGlyph = ({ size = 28 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path d="M5 4h14v3a7 7 0 0 1-14 0V4Z" fill="#c89525" />
    <path d="M5 5H3.4a2.6 2.6 0 0 0 3.1 3M19 5h1.6a2.6 2.6 0 0 1-3.1 3" stroke="#c89525" strokeWidth="1.5" fill="none" />
    <rect x="10.5" y="11" width="3" height="5" fill="#a07415" />
    <rect x="7.5" y="16" width="9" height="3" rx="1" fill="#0e244a" />
  </svg>
);

function generateSeeds(n) {
  if (n <= 2) return [1, 2];
  const prev = generateSeeds(n / 2);
  const result = [];
  for (const s of prev) result.push(s, n + 1 - s);
  return result;
}

function buildEmptyBracket(teamCount) {
  if (!teamCount || teamCount < 2) return null;
  const n = Math.pow(2, Math.ceil(Math.log2(Math.max(teamCount, 2))));
  const phases = PHASE_ORDER.filter(p => n >= PHASE_MIN_TEAMS[p]);
  if (!phases.length) return null;

  const seeds = generateSeeds(n);

  return {
    rounds: phases.map((phase, phaseIndex) => {
      const matchCount = n / Math.pow(2, phaseIndex + 1);
      const isFirstRound = phaseIndex === 0;
      return {
        phase,
        matches: Array.from({ length: matchCount }, (_, j) => {
          let homeTeam = null;
          let awayTeam = null;
          if (isFirstRound) {
            const s1 = seeds[j * 2];
            const s2 = seeds[j * 2 + 1];
            if (s1 && s1 <= teamCount) homeTeam = { name: `${s1}º Lugar`, isPlaceholder: true, seed: s1 };
            if (s2 && s2 <= teamCount) awayTeam = { name: `${s2}º Lugar`, isPlaceholder: true, seed: s2 };
          }
          return {
            id:          `empty-${phase}-${j}`,
            homeTeam,
            awayTeam,
            status:      'PENDING',
            scheduledAt: null,
            venue:       null,
            winner:      null,
            homeScore:   null,
            awayScore:   null,
          };
        }),
      };
    }),
  };
}

function knockoutTeamCount(tournament, standings) {
  if (!tournament) return 0;
  const { format, teamsAdvancing, groupCount } = tournament;
  if (format === 'GROUPS')   return (teamsAdvancing || 1) * (groupCount || 1);
  if (format === 'LEAGUE')   return teamsAdvancing || 0;
  if (format === 'KNOCKOUT') return standings?.length || 0;
  return 0;
}

const SLOT_MIN  = 110;
const HEADER_H  = 38;

// ─── Eyebrow (nome do torneio · Fase Final) ──────────────────────────────────
function BracketEyebrow({ tournament }) {
  return (
    <div className="wb-eyebrow">
      <span className="wb-eyebrow-main">{tournament?.name || 'Campeonato'}</span>
      <span className="wb-eyebrow-dot">·</span>
      <span className="wb-eyebrow-sub">Fase Final</span>
    </div>
  );
}

function BracketLegend() {
  return (
    <div className="wb-legend">
      <span className="wb-legend-dot wb-legend-dot--won" />
      <span>Classificado</span>
      <span className="wb-legend-dot wb-legend-dot--tbd" style={{ marginLeft: 20 }} />
      <span>A definir</span>
    </div>
  );
}

// ─── Chip de local (inline, sem link externo no bracket) ─────────────────────
function VenueTag({ venue }) {
  if (!venue) return null;
  const label = [venue.name, venue.city].filter(Boolean).join(' · ');
  return (
    <span className="wb-venue">
      <svg viewBox="0 0 14 18" width="8" height="10" style={{ flexShrink: 0 }}>
        <path d="M7 0C4.24 0 2 2.24 2 5c0 3.75 5 11 5 11s5-7.25 5-11c0-2.76-2.24-5-5-5z"
              fill="currentColor" opacity="0.8" />
        <circle cx="7" cy="5" r="2" fill="white" />
      </svg>
      {label}
    </span>
  );
}

function TeamSlot({ team, won, finished, score, showClass }) {
  const isPlaceholder = team?.isPlaceholder;
  const isTbd = !team;
  const decided = finished && !isTbd && !isPlaceholder;
  const rowClass = `wb-row${won ? ' wb-row--won' : ''}${(isTbd || isPlaceholder) ? ' wb-row--tbd' : ''}`;
  return (
    <div className={rowClass}>
      {isPlaceholder
        ? <span className="wb-seed-badge">{team.seed}º</span>
        : <TeamLogo name={team?.name || '?'} logoUrl={team?.logoUrl || null} size={20} shape="shield" style={{ marginRight: 6 }} />
      }
      <span className="wb-name">{team?.name || 'A definir'}</span>
      {finished && score != null && <span className={`wb-score${won ? ' wb-score--won' : ''}`}>{score}</span>}
      {showClass && decided && (
        won
          ? <span className="wb-class wb-class--won">&#10003; Class.</span>
          : <span className="wb-class wb-class--out">Fora</span>
      )}
    </div>
  );
}

function MatchCard({ match, showClass = false }) {
  if (!match) return null;
  const winnerId    = match.winner?.id;
  const homeWon     = !!(winnerId && match.homeTeam?.id === winnerId);
  const awayWon     = !!(winnerId && match.awayTeam?.id === winnerId);
  const finished    = match.status === 'FINISHED';
  const isPreview   = !match.homeTeam?.id && !match.awayTeam?.id;

  const fmtDate = match.scheduledAt
    ? new Date(match.scheduledAt).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div className={`wb-card${finished ? ' wb-card--done' : ''}${isPreview ? ' wb-card--preview' : ''}`}>
      {(fmtDate || match.venue) && (
        <div className="wb-card-meta">
          {fmtDate && <div className="wb-date">{fmtDate}</div>}
          {match.venue && <VenueTag venue={match.venue} />}
        </div>
      )}

      <TeamSlot team={match.homeTeam} won={homeWon} finished={finished} score={match.homeScore} showClass={showClass} />
      <div className="wb-sep" />
      <TeamSlot team={match.awayTeam} won={awayWon} finished={finished} score={match.awayScore} showClass={showClass} />
      {match.homePenalties != null && (
        <div className="wb-pen">Pên: {match.homePenalties} × {match.awayPenalties}</div>
      )}
    </div>
  );
}

function RoundColumn({ round, colH, side, feeds }) {
  return (
    <div className={`wb-col wb-col--${side}${feeds ? ' wb-col--feeds' : ''}`} style={{ height: colH }}>
      <div className="wb-col-header">{PHASE_SHORT[round.phase]}</div>
      <div className="wb-col-matches" style={{ height: colH - HEADER_H }}>
        {round.matches.map(m => (
          <div key={m.id} className="wb-slot">
            <MatchCard match={m} showClass={feeds} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Slot de finalista (vencedor de cada chave) ──────────────────────────────
function FinalSlot({ team, side, finished, score, won }) {
  const set = !!team?.id;
  if (!set) {
    return (
      <div className="wb-fin-slot wb-fin-slot--tbd">
        <span className="wb-fin-slot-mark">?</span>
        <div className="wb-fin-slot-body">
          <div className="wb-fin-slot-name wb-fin-slot-name--tbd">A definir</div>
          <div className="wb-fin-slot-sub">Vencedor · {side}</div>
        </div>
      </div>
    );
  }
  return (
    <div className={`wb-fin-slot wb-fin-slot--set${won ? ' wb-fin-slot--won' : ''}`}>
      <TeamLogo name={team.name} logoUrl={team.logoUrl || null} size={26} shape="shield" />
      <div className="wb-fin-slot-body">
        <div className="wb-fin-slot-name">{team.name}</div>
        <div className="wb-fin-slot-sub wb-fin-slot-sub--set">&#10003; Classificado · {side}</div>
      </div>
      {finished && score != null && <span className="wb-fin-slot-score">{score}</span>}
    </div>
  );
}

// ─── Card central da Final ───────────────────────────────────────────────────
function FinalCard({ match }) {
  const home     = match?.homeTeam;
  const away     = match?.awayTeam;
  const finished = match?.status === 'FINISHED';
  const winnerId = match?.winner?.id;
  const homeWon  = !!(winnerId && home?.id === winnerId);
  const awayWon  = !!(winnerId && away?.id === winnerId);
  const bothSet  = !!(home?.id && away?.id);

  const date = match?.scheduledAt ? new Date(match.scheduledAt) : null;
  const day  = date && date.getDate();
  const mon  = date && date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase();
  const year = date && date.getFullYear();
  const time = date && date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="wb-fin">
      <div className="wb-fin-emblem">
        <span className="wb-fin-emblem-glow" />
        <span className="wb-fin-emblem-disc"><TrophyGlyph size={30} /></span>
      </div>

      <div className="wb-fin-card">
        <div className="wb-fin-eyebrow">A Grande Decisão</div>
        <div className="wb-fin-title">FINAL</div>

        {date && (
          <div className="wb-fin-date">
            <span className="wb-fin-date-day">{day} {mon}</span>
            <span className="wb-fin-date-year">{year}</span>
            <span className="wb-fin-date-time">{time}</span>
          </div>
        )}
        {match?.venue && (
          <div className="wb-fin-venue"><VenueTag venue={match.venue} /></div>
        )}

        <div className="wb-fin-slots">
          <FinalSlot team={home} side="Chave A" finished={finished} score={match?.homeScore} won={homeWon} />
          <div className="wb-fin-x"><span>&#215;</span></div>
          <FinalSlot team={away} side="Chave B" finished={finished} score={match?.awayScore} won={awayWon} />
        </div>

        {!finished && (
          bothSet
            ? (
              <div className="wb-fin-pill wb-fin-pill--ready">
                <TrophyGlyph size={15} />
                <span>Decisão definida</span>
              </div>
            )
            : (
              <div className="wb-fin-pill wb-fin-pill--wait">
                <span className="wb-fin-pill-dot" />
                <span className="wb-fin-pill-text">Aguardando semifinais</span>
              </div>
            )
        )}
      </div>
    </div>
  );
}

export default function TournamentBracket({ bracket, tournament, standings }) {
  const hasRealBracket = bracket?.rounds?.length > 0;

  const resolved = hasRealBracket
    ? bracket
    : buildEmptyBracket(knockoutTeamCount(tournament, standings));

  if (!resolved) {
    return (
      <>
        <BracketEyebrow tournament={tournament} />
        <div className="camp-empty">
          <p>O chaveamento será exibido quando o torneio avançar para o mata-mata.</p>
        </div>
      </>
    );
  }

  const sorted = [...resolved.rounds].sort(
    (a, b) => PHASE_ORDER.indexOf(a.phase) - PHASE_ORDER.indexOf(b.phase)
  );

  const finalRound        = sorted.find(r => r.phase === 'FINAL');
  const progressionRounds = sorted.filter(r => r.phase !== 'FINAL');
  const finalMatch        = finalRound?.matches?.[0] ?? null;
  const champion          = finalMatch?.winner;

  const outerMatchCount = progressionRounds.length > 0
    ? Math.ceil(progressionRounds[0].matches.length / 2)
    : 1;

  const colH = Math.max(outerMatchCount * SLOT_MIN + HEADER_H, 240);

  const lastIdx = progressionRounds.length - 1;

  const leftRounds = progressionRounds.map((r, i) => ({
    ...r,
    feeds: i === lastIdx,
    matches: r.matches.slice(0, Math.ceil(r.matches.length / 2)),
  }));

  const rightRounds = [...progressionRounds]
    .reverse()
    .map((r, i) => ({
      ...r,
      feeds: i === 0,
      matches: r.matches.slice(Math.ceil(r.matches.length / 2)),
    }));

  const ChampionBanner = champion ? (
    <div className="wb-champion">
      <span className="wb-champion-trophy"><TrophyGlyph size={46} /></span>
      <span className="wb-champion-label">CAMPEÃO</span>
      <TeamLogo name={champion.name} logoUrl={champion.logoUrl || null} size={72} shape="shield" />
      <span className="wb-champion-name">{champion.name}</span>
    </div>
  ) : null;

  // Só Final (2 times) — sem rodadas anteriores
  if (progressionRounds.length === 0) {
    return (
      <>
        <BracketEyebrow tournament={tournament} />
        <div className="wb-wrapper">
          {ChampionBanner}
          <div className="wb-top-bar">
            <span className="wb-top-bar-side">Chave A</span>
            <span className="wb-top-bar-center">Mata-Mata</span>
            <span className="wb-top-bar-side">Chave B</span>
          </div>
          <div className="wb-scroll-wrap">
            <div className="wb-arena wb-arena--final-only">
              <div className="wb-trophy-bg" aria-hidden="true"><TrophyGlyph size={200} /></div>
              <div className="wb-fin-zone"><FinalCard match={finalMatch} /></div>
            </div>
          </div>
          <BracketLegend />
        </div>
      </>
    );
  }

  return (
    <>
      <BracketEyebrow tournament={tournament} />
      <div className="wb-wrapper">
        {ChampionBanner}

        <div className="wb-top-bar">
          <span className="wb-top-bar-side">Chave A</span>
          <span className="wb-top-bar-center">Mata-Mata</span>
          <span className="wb-top-bar-side">Chave B</span>
        </div>

        <div className="wb-scroll-wrap">
          <div className="wb-arena" style={{ minHeight: colH + 20 }}>
            <div className="wb-trophy-bg" aria-hidden="true"><TrophyGlyph size={200} /></div>

            <div className="wb-side wb-side--left">
              {leftRounds.map(round => (
                <RoundColumn key={round.phase} round={round} colH={colH} side="left" feeds={round.feeds} />
              ))}
            </div>

            <div className="wb-fin-zone"><FinalCard match={finalMatch} /></div>

            <div className="wb-side wb-side--right">
              {rightRounds.map(round => (
                <RoundColumn key={round.phase + '_r'} round={round} colH={colH} side="right" feeds={round.feeds} />
              ))}
            </div>
          </div>
        </div>

        <BracketLegend />
      </div>
    </>
  );
}
